import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAuthStore } from '@stores/AuthStore.js'
import { hydrateClientStores } from '@stores/hydrateClientStores.js'

/**
 * Account-page state. Not persisted: the profile is re-fetched on every visit.
 */
export const useProfileStore = defineStore('profileStore', () => {
  const authStore = useAuthStore()

  const profile = ref(null)
  const orders = ref([])
  const ordersPage = ref(1)
  const ordersLastPage = ref(1)
  const ordersLoading = ref(false)
  const ordersLoaded = ref(false) // a first successful orders load has happened
  const ordersLoadError = ref(false)
  const errors = ref({})
  const saveError = ref(false)
  const loading = ref(false)
  const loaded = ref(false) // a first successful profile load has happened
  const loadError = ref(false)

  const headers = () => ({ Authorization: `Bearer ${authStore.token}` })

  // Invalid/expired session: hard-reload to '/' so no stale in-memory state
  // survives. Returns true when it handled the error.
  const handleAuthError = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      authStore.clearSession()
      if (!import.meta.env.SSR) window.location.href = '/'
      return true
    }
    return false
  }

  // Stale-while-revalidate: only show the blocking spinner on the first load.
  // A revisit keeps the cached profile on screen and refreshes silently.
  const loadProfile = async () => {
    const spin = !loaded.value
    if (spin) loading.value = true
    loadError.value = false
    try {
      const { data } = await axios.get('/api/client/profile', { headers: headers() })
      profile.value = data
      loaded.value = true
    } catch (error) {
      if (!handleAuthError(error)) loadError.value = true
    } finally {
      if (spin) loading.value = false
    }
  }

  // Orders are paginated at 10/page server-side; each page replaces the list.
  const loadOrders = async (page = 1) => {
    ordersLoading.value = true
    ordersLoadError.value = false
    try {
      const { data } = await axios.get('/api/client/orders', {
        params: { page },
        headers: headers(),
      })
      orders.value = data.data ?? []
      ordersLastPage.value = data.meta?.last_page ?? 1
      // Clamp: the server echoes out-of-range pages when the total shrinks mid-session.
      ordersPage.value = Math.max(1, Math.min(data.meta?.current_page ?? page, ordersLastPage.value))
      ordersLoaded.value = true
    } catch (error) {
      if (!handleAuthError(error)) ordersLoadError.value = true
    } finally {
      ordersLoading.value = false
    }
  }

  const reset = () => {
    profile.value = null
    orders.value = []
    ordersPage.value = 1
    ordersLastPage.value = 1
    ordersLoading.value = false
    ordersLoaded.value = false
    ordersLoadError.value = false
    errors.value = {}
    saveError.value = false
    loading.value = false
    loaded.value = false
    loadError.value = false
  }

  /**
   * Refresh the service-form stores from the just-saved profile so the rest
   * of the site reflects the new data.
   */
  const propagate = () => {
    if (profile.value) hydrateClientStores(profile.value)
  }

  const savePersonal = async (payload) => {
    errors.value = {}
    saveError.value = false
    try {
      const { data } = await axios.patch('/api/client/profile', payload, { headers: headers() })
      profile.value = data
      propagate()
      return true
    } catch (error) {
      if (error?.response?.status === 422) {
        errors.value = error.response.data?.errors ?? {}
      } else if (!handleAuthError(error)) {
        saveError.value = true
      }
      return false
    }
  }

  const saveAddress = async (type, gaddress) => {
    errors.value = {}
    saveError.value = false
    try {
      const { data } = await axios.put(`/api/client/addresses/${type}`, { gaddress }, { headers: headers() })
      profile.value = data
      propagate()
      return true
    } catch (error) {
      if (error?.response?.status === 422) {
        errors.value = error.response.data?.errors ?? {}
      } else if (!handleAuthError(error)) {
        saveError.value = true
      }
      return false
    }
  }

  return {
    profile,
    orders,
    ordersPage,
    ordersLastPage,
    ordersLoading,
    ordersLoaded,
    ordersLoadError,
    errors,
    saveError,
    loading,
    loadError,
    reset,
    loadProfile,
    loadOrders,
    savePersonal,
    saveAddress,
  }
})
